import { useEffect, useMemo, useState } from "react";
import {
  Archive,
  Building2,
  ChevronRight,
  ClipboardCheck,
  FileText,
  Globe2,
  MapPin,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  UserRoundCheck,
} from "lucide-react";
import type { PageContext } from "../App";
import { api, getApiError } from "../api";
import {
  Button,
  Field,
  InlineError,
  Input,
  MarketplaceMark,
  Modal,
  PageHeader,
  Panel,
  Select,
  StatePanel,
  StatusPill,
  Textarea,
  formatDate,
} from "../components/ui";
import { useAsync } from "../hooks";
import {
  MARKETPLACE_CODES,
  MARKETPLACE_META,
  type MarketplaceCode,
  type SettingsTemplate,
  type TemplateKind,
} from "../types";

type TemplateFilter = "all" | TemplateKind;

const templateMeta: Record<
  TemplateKind,
  { label: string; description: string; icon: React.ReactNode }
> = {
  listing: {
    label: "Listing",
    description: "Wiederverwendbare Katalogattribute",
    icon: <FileText size={20} />,
  },
  safety: {
    label: "Produktsicherheit",
    description: "Geprüfte Sicherheitsangaben für LEGO, Duftöle und weitere Produkte",
    icon: <ShieldCheck size={20} />,
  },
  compliance: {
    label: "Compliance",
    description: "GPSR-, Warn- und Sicherheitstexte",
    icon: <ShieldCheck size={20} />,
  },
  manufacturer: {
    label: "Hersteller",
    description: "Hersteller- und Kontaktdaten",
    icon: <Building2 size={20} />,
  },
  responsible_person: {
    label: "EU-Verantwortlicher",
    description: "Verantwortliche Person in der EU",
    icon: <UserRoundCheck size={20} />,
  },
};

interface TemplateFormState {
  id?: string;
  name: string;
  kind: TemplateKind;
  marketplace: MarketplaceCode | "";
  productType: string;
  attributesJson: string;
  company: string;
  contactName: string;
  addressLine: string;
  postalCode: string;
  city: string;
  country: string;
  email: string;
  phone: string;
  note: string;
  originalValues: Record<string, unknown>;
}

const emptyForm: TemplateFormState = {
  name: "",
  kind: "listing",
  marketplace: "",
  productType: "",
  attributesJson: "{}",
  company: "",
  contactName: "",
  addressLine: "",
  postalCode: "",
  city: "",
  country: "DE",
  email: "",
  phone: "",
  note: "",
  originalValues: {},
};

export function TemplatesPage({ notify }: PageContext) {
  const templates = useAsync(api.templates, []);
  const [filter, setFilter] = useState<TemplateFilter>("all");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<TemplateFormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return (templates.data ?? []).filter(
      (template) =>
        (filter === "all" || template.kind === filter) &&
        (!normalized ||
          template.name.toLowerCase().includes(normalized) ||
          String(template.values.company ?? "").toLowerCase().includes(normalized) ||
          String(template.values.productType ?? "").toLowerCase().includes(normalized)),
    );
  }, [templates.data, query, filter]);

  const openEdit = (template: SettingsTemplate) => {
    const values = template.values;
    setEditing({
      id: template.id,
      name: template.name,
      kind: normalizeKind(template.kind),
      marketplace: template.marketplace ?? "",
      productType: valueString(values.productType) || valueString(values.product_type),
      attributesJson: JSON.stringify(
        isPlainRecord(values.attributes) ? values.attributes : {},
        null,
        2,
      ),
      company: valueString(values.company),
      contactName: valueString(values.contactName),
      addressLine: valueString(values.addressLine),
      postalCode: valueString(values.postalCode),
      city: valueString(values.city),
      country: valueString(values.country) || "DE",
      email: valueString(values.email),
      phone: valueString(values.phone),
      note: valueString(values.note),
      originalValues: values,
    });
    setFormError(null);
  };

  const save = async () => {
    if (!editing || !editing.name.trim()) {
      setFormError("Bitte gib der Vorlage einen eindeutigen Namen.");
      return;
    }
    let listingAttributes: Record<string, unknown> = {};
    if (editing.kind === "listing" || editing.kind === "safety") {
      if (!editing.productType.trim()) {
        setFormError("Für diese Vorlage ist der Amazon-Produkttyp erforderlich.");
        return;
      }
      if (editing.kind === "safety" && !editing.marketplace) {
        setFormError("Sicherheitsvorlagen benötigen einen bestimmten Marktplatz.");
        return;
      }
      try {
        const parsed = JSON.parse(editing.attributesJson);
        if (!isPlainRecord(parsed)) {
          throw new Error("Attribute müssen als JSON-Objekt angegeben werden.");
        }
        listingAttributes = parsed;
      } catch (reason) {
        setFormError(
          reason instanceof Error ? `Attribut-JSON: ${reason.message}` : "Attribut-JSON ist ungültig.",
        );
        return;
      }
    }
    setSaving(true);
    setFormError(null);
    const payload = {
      name: editing.name.trim(),
      kind: editing.kind,
      marketplace: editing.marketplace || null,
      values:
        editing.kind === "listing" || editing.kind === "safety"
          ? {
              ...editing.originalValues,
              ...(editing.kind === "safety" ? { version: 1, category: editing.originalValues.category ?? "other" } : {}),
              productType: editing.productType.trim().toUpperCase(),
              marketplace: editing.marketplace || undefined,
              attributes: listingAttributes,
              note: editing.note,
            }
          : {
              company: editing.company,
              contactName: editing.contactName,
              addressLine: editing.addressLine,
              postalCode: editing.postalCode,
              city: editing.city,
              country: editing.country,
              email: editing.email,
              phone: editing.phone,
              note: editing.note,
            },
    };
    try {
      if (editing.id) {
        await api.updateTemplate(editing.id, payload);
        notify("Vorlage wurde aktualisiert.", "success");
      } else {
        await api.createTemplate(payload);
        notify("Neue Vorlage wurde gespeichert.", "success");
      }
      setEditing(null);
      await templates.reload();
    } catch (reason) {
      setFormError(getApiError(reason));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (template: SettingsTemplate) => {
    if (!window.confirm(`Vorlage „${template.name}“ wirklich löschen?`)) return;
    try {
      await api.deleteTemplate(template.id);
      notify("Vorlage wurde gelöscht.", "success");
      await templates.reload();
    } catch (reason) {
      notify(getApiError(reason), "error");
    }
  };

  return (
    <div className="templates-page">
      <PageHeader
        eyebrow="Vorlagen"
        title="Katalog- & Compliance-Daten wiederverwenden"
        description="Pflege Hersteller, EU-Verantwortliche, GPSR-Texte und wiederkehrende Listingwerte einmal zentral."
        actions={
          <Button onClick={() => setEditing({ ...emptyForm })} icon={<Plus size={17} />}>
            Neue Vorlage
          </Button>
        }
      />

      <section className="template-type-grid">
        <button
          className={filter === "all" ? "is-active" : ""}
          onClick={() => setFilter("all")}
        >
          <span><Archive size={20} /></span>
          <div><strong>Alle Vorlagen</strong><small>{templates.data?.length ?? 0} Einträge</small></div>
          <ChevronRight size={16} />
        </button>
        {(Object.entries(templateMeta) as Array<[TemplateKind, (typeof templateMeta)[TemplateKind]]>).map(
          ([kind, meta]) => (
            <button
              key={kind}
              className={filter === kind ? "is-active" : ""}
              onClick={() => setFilter(kind)}
            >
              <span>{meta.icon}</span>
              <div>
                <strong>{meta.label}</strong>
                <small>{(templates.data ?? []).filter((item) => item.kind === kind).length} Einträge</small>
              </div>
              <ChevronRight size={16} />
            </button>
          ),
        )}
      </section>

      <Panel className="templates-panel">
        <div className="templates-toolbar">
          <div>
            <h2>{filter === "all" ? "Alle Vorlagen" : templateMeta[filter].label}</h2>
            <p>
              {filter === "all"
                ? "Listing- und Sicherheitsvorlagen lassen sich in den jeweiligen Editoren anwenden."
                : templateMeta[filter].description}
            </p>
          </div>
          <div className="matrix-search templates-search">
            <Search size={17} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Vorlagen durchsuchen …"
              aria-label="Vorlagen durchsuchen"
            />
          </div>
        </div>

        {templates.error ? (
          <InlineError message={templates.error} onRetry={() => void templates.reload()} />
        ) : templates.loading ? (
          <div className="template-card-grid">
            {Array.from({ length: 3 }, (_, index) => <TemplateSkeleton key={index} />)}
          </div>
        ) : filtered.length === 0 ? (
          <StatePanel
            type="empty"
            title={query ? "Keine passende Vorlage" : "Noch keine Vorlage angelegt"}
            description={
              query
                ? "Passe den Suchbegriff oder den Vorlagentyp an."
                : "Lege Hersteller-, Compliance- oder Listingwerte als erste Vorlage an."
            }
            action={
              !query ? (
                <Button onClick={() => setEditing({ ...emptyForm })} icon={<Plus size={16} />}>
                  Erste Vorlage anlegen
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="template-card-grid">
            {filtered.map((template) => {
              const kind = normalizeKind(template.kind);
              const meta = templateMeta[kind];
              return (
                <article className="template-card" key={template.id}>
                  <header>
                    <span className={`template-card__icon template-card__icon--${kind}`}>
                      {meta.icon}
                    </span>
                    <div>
                      <StatusPill status="neutral" label={meta.label} dot={false} />
                      <h3>{template.name}</h3>
                    </div>
                  </header>
                  <div className="template-card__content">
                    {valueString(template.values.company) && (
                      <p><Building2 size={14} /> {valueString(template.values.company)}</p>
                    )}
                    {(kind === "listing" || kind === "safety") && valueString(template.values.productType) && (
                      <p><FileText size={14} /> Produkttyp {valueString(template.values.productType)}</p>
                    )}
                    {(kind === "listing" || kind === "safety") && isPlainRecord(template.values.attributes) && (
                      <p>
                        <ClipboardCheck size={14} />{" "}
                        {Object.keys(template.values.attributes).length} strukturierte Attribute
                      </p>
                    )}
                    {(valueString(template.values.city) || valueString(template.values.country)) && (
                      <p>
                        <MapPin size={14} />
                        {[valueString(template.values.city), valueString(template.values.country)]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    )}
                    {valueString(template.values.note) && (
                      <p className="template-card__note">{valueString(template.values.note)}</p>
                    )}
                  </div>
                  <footer>
                    <span>
                      {template.marketplace ? (
                        <MarketplaceMark code={template.marketplace} />
                      ) : (
                        "Alle Marktplätze"
                      )}{" "}
                      · {formatDate(template.updatedAt)}
                    </span>
                    <div>
                      <button onClick={() => openEdit(template)} aria-label="Vorlage bearbeiten">
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => void remove(template)} aria-label="Vorlage löschen">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </footer>
                </article>
              );
            })}
          </div>
        )}
      </Panel>

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Vorlage bearbeiten" : "Neue Vorlage anlegen"}
        description="Die Werte bleiben intern und werden erst in einer bestätigten Vorschau an Amazon übermittelt."
        size="large"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>Abbrechen</Button>
            <Button loading={saving} onClick={save} icon={<ClipboardCheck size={16} />}>
              Vorlage speichern
            </Button>
          </>
        }
      >
        {editing && (
          <div className="template-form">
            {formError && <InlineError message={formError} />}
            <div className="form-grid form-grid--two">
              <Field label="Name der Vorlage *" className="form-grid__wide">
                <Input
                  value={editing.name}
                  onChange={(event) => setEditing({ ...editing, name: event.target.value })}
                  placeholder="z. B. LEGO Herstellerdaten"
                />
              </Field>
              <Field label="Vorlagentyp">
                <Select
                  value={editing.kind}
                  onChange={(event) =>
                    setEditing({ ...editing, kind: event.target.value as TemplateKind, originalValues: {} })
                  }
                >
                  {(Object.entries(templateMeta) as Array<[TemplateKind, (typeof templateMeta)[TemplateKind]]>).map(
                    ([kind, meta]) => <option key={kind} value={kind}>{meta.label}</option>,
                  )}
                </Select>
              </Field>
              <Field label="Marketplace">
                <Select
                  value={editing.marketplace}
                  onChange={(event) =>
                    setEditing({
                      ...editing,
                      marketplace: event.target.value as MarketplaceCode | "",
                    })
                  }
                >
                  <option value="">Alle Marktplätze</option>
                  {MARKETPLACE_CODES.map((code) => (
                    <option key={code} value={code}>{MARKETPLACE_META[code].label} ({code})</option>
                  ))}
                </Select>
              </Field>
              {editing.kind === "listing" || editing.kind === "safety" ? (
                <>
                  <Field
                    label="Amazon-Produkttyp *"
                    hint="Muss exakt zum aktuellen Amazon-Produkttyp passen. Sicherheitsvorlagen lassen sich direkt in der Sicherheitsprüfung speichern."
                    className="form-grid__wide"
                  >
                    <Input
                      value={editing.productType}
                      onChange={(event) =>
                        setEditing({
                          ...editing,
                          productType: event.target.value.toUpperCase(),
                        })
                      }
                      placeholder="z. B. TOYS_AND_GAMES"
                    />
                  </Field>
                  <Field
                    label="Strukturierte Amazon-Attribute *"
                    hint={editing.kind === "safety" ? "Die strukturierten Sicherheitswerte bleiben zusammen mit den Angaben zur Vorlage erhalten. Bequemer bearbeitest und speicherst du sie in der Sicherheitsprüfung." : "Nur dieses values.attributes-Objekt wird im Uploader angeboten und gemergt."}
                    className="form-grid__wide"
                  >
                    <Textarea
                      className="template-json-editor"
                      rows={13}
                      spellCheck={false}
                      value={editing.attributesJson}
                      onChange={(event) =>
                        setEditing({ ...editing, attributesJson: event.target.value })
                      }
                      placeholder={'{\n  "brand": [{"value": "LEGO"}]\n}'}
                    />
                  </Field>
                  <Field label="Interner Anwendungshinweis" className="form-grid__wide">
                    <Textarea
                      rows={3}
                      value={editing.note}
                      onChange={(event) => setEditing({ ...editing, note: event.target.value })}
                      placeholder="Wann soll diese Vorlage verwendet werden?"
                    />
                  </Field>
                </>
              ) : (
                <>
                  <Field label="Firma / Organisation">
                    <Input
                      value={editing.company}
                      onChange={(event) => setEditing({ ...editing, company: event.target.value })}
                      placeholder="Firmenname"
                    />
                  </Field>
                  <Field label="Kontaktperson">
                    <Input
                      value={editing.contactName}
                      onChange={(event) => setEditing({ ...editing, contactName: event.target.value })}
                      placeholder="Vor- und Nachname"
                    />
                  </Field>
                  <Field label="Straße & Hausnummer" className="form-grid__wide">
                    <Input
                      value={editing.addressLine}
                      onChange={(event) => setEditing({ ...editing, addressLine: event.target.value })}
                      placeholder="Anschrift laut Produktunterlagen"
                    />
                  </Field>
                  <Field label="PLZ">
                    <Input
                      value={editing.postalCode}
                      onChange={(event) => setEditing({ ...editing, postalCode: event.target.value })}
                    />
                  </Field>
                  <Field label="Ort">
                    <Input
                      value={editing.city}
                      onChange={(event) => setEditing({ ...editing, city: event.target.value })}
                    />
                  </Field>
                  <Field label="Land">
                    <Input
                      value={editing.country}
                      onChange={(event) =>
                        setEditing({
                          ...editing,
                          country: event.target.value.toUpperCase(),
                        })
                      }
                      maxLength={2}
                      placeholder="DE"
                    />
                  </Field>
                  <Field label="E-Mail">
                    <Input
                      type="email"
                      value={editing.email}
                      onChange={(event) => setEditing({ ...editing, email: event.target.value })}
                    />
                  </Field>
                  <Field label="Telefon">
                    <Input
                      value={editing.phone}
                      onChange={(event) => setEditing({ ...editing, phone: event.target.value })}
                    />
                  </Field>
                  <Field label="Inhalt / Hinweis" className="form-grid__wide">
                    <Textarea
                      rows={5}
                      value={editing.note}
                      onChange={(event) => setEditing({ ...editing, note: event.target.value })}
                      placeholder="GPSR-Hinweis, Warntext oder interner Anwendungshinweis …"
                    />
                  </Field>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function TemplateSkeleton() {
  return (
    <div className="template-card template-card--skeleton">
      <span /><i /><i /><i />
    </div>
  );
}

function normalizeKind(value: string): TemplateKind {
  if (value === "safety" || value === "compliance" || value === "manufacturer" || value === "responsible_person") {
    return value;
  }
  return "listing";
}

function valueString(value: unknown) {
  return typeof value === "string" ? value : "";
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

